import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    House, ReceiptX, ShoppingCart, Bank, BookOpen, ChartLineUp,
    Notepad, Users, SignOut, ClockCounterClockwise, Coins, HandCoins, GearSix, Wallet, UsersThree, CheckCircle, Package, Cube, Hammer, Calendar, Receipt, Fingerprint, CurrencyInr, List, Star, UploadSimple, Sparkle, ShieldCheck, Key, DownloadSimple,
} from "@phosphor-icons/react";

// Each nav item now carries an accent colour that lights up on hover/active.
// Role rules (Feb 2026):
//  – founder: sees everything
//  – accountant: bookkeeping subset (Bank, Cash vouchers, Ledgers, Journals, CoA)
//  – purchase: purchase invoices + vendor ledger drill + petty cash + leave
//  – dispatch (renamed from sales): sales invoices + advances + dispatch team payroll + petty cash + leave
//  – production, sales_staff, customer_experience, hr: petty cash + leave (+ their departmental modules once built)
//  – employee (Phase 4): self-serve — own payslips + own leave requests only. NO other menu items.
//  – FOUNDER-ONLY (API-enforced): P&L, Loans, Fixed Assets. Compliance + Expenses are accountant-visible.
const ALL_STAFF = ["founder", "accountant", "hr", "purchase", "dispatch", "production", "sales_staff", "customer_experience"];
const WITH_EMPLOYEE = [...ALL_STAFF, "employee"];
const NAV = [
    { section: "Overview", accent: "#0dcaf0", items: [
        // Dashboard is founder-only; other roles are redirected from "/" to their role-appropriate landing screen.
        { to: "/", label: "Dashboard", icon: House, roles: ["founder"] },
        { to: "/ask", label: "Ask Finance AI", icon: Sparkle, roles: ["founder"] },
        { to: "/all-approvals", label: "All approvals", icon: CheckCircle, roles: ["founder", "accountant"] },
        { to: "/pending-payouts", label: "Pending payouts", icon: HandCoins, roles: ["founder", "accountant"] },
        { to: "/cash-bank", label: "Cash & bank position", icon: Coins, roles: ["founder", "accountant"] },
    ]},
    { section: "Capture", accent: "#fd7e14", items: [
        { to: "/purchase", label: "Purchase invoices", icon: ShoppingCart, roles: ["founder", "purchase", "accountant"] },
        { to: "/payment-requests", label: "Payment requests", icon: HandCoins, roles: ["founder", "purchase", "accountant"] },
        { to: "/sales", label: "Sales invoices", icon: ReceiptX, roles: ["founder", "dispatch", "accountant"] },
        { to: "/advances", label: "Customer advances", icon: Coins, roles: ["founder", "dispatch", "accountant"] },
        { to: "/bank", label: "Bank statements", icon: Bank, roles: ["founder", "accountant"] },
        { to: "/cash", label: "Cash vouchers", icon: HandCoins, roles: ["founder", "accountant"] },
        { to: "/fiat", label: "Fiat vouchers", icon: CurrencyInr, roles: ["founder", "accountant", "purchase", "sales_staff", "dispatch"] },
    ]},
    { section: "Team", accent: "#f59e0b", items: [
        { to: "/employees", label: "Employees", icon: UsersThree, roles: ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"] },
        { to: "/payroll", label: "Payroll", icon: HandCoins, roles: ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"] },
        { to: "/payroll-approvals", label: "Payroll approvals", icon: CheckCircle, roles: ["founder", "hr"] },
        { to: "/salary-reconcile", label: "Salary reconcile", icon: CheckCircle, roles: ["founder", "accountant"] },
        { to: "/employee-advances", label: "Advances (staff)", icon: HandCoins, roles: WITH_EMPLOYEE },
        { to: "/contract-workers", label: "Contract workers", icon: Hammer, roles: ["founder", "accountant", "hr", "production", "dispatch"] },
        { to: "/contract-payouts", label: "Contract payouts", icon: HandCoins, roles: ["founder", "accountant", "hr", "production", "dispatch"] },
        { to: "/biometric", label: "Biometric attendance", icon: Fingerprint, roles: ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"] },
        { to: "/performance-reviews", label: "Performance reviews", icon: Star, roles: ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"] },
        { to: "/leaves", label: "Leaves", icon: Calendar, roles: WITH_EMPLOYEE },
        { to: "/my-payslips", label: "My payslips", icon: Receipt, roles: ["founder"] },
        // Petty cash open to every authenticated role — everyone raises requests
        { to: "/petty-cash", label: "Petty cash", icon: Wallet, roles: WITH_EMPLOYEE },
        { to: "/expense-claims", label: "Expense claims", icon: Receipt, roles: WITH_EMPLOYEE },
    ]},
    { section: "Books", accent: "#20c997", items: [
        // Ledgers/CoA stay open to Accountant + Purchase (vendor drill) + Dispatch (customer drill)
        { to: "/ledgers", label: "Ledgers", icon: BookOpen, roles: ["founder", "accountant", "purchase", "dispatch"] },
        { to: "/reports/payables", label: "Payables", icon: ChartLineUp, roles: ["founder", "accountant", "purchase"] },
        { to: "/reports/receivables", label: "Receivables", icon: ChartLineUp, roles: ["founder", "accountant", "dispatch"] },
        { to: "/reports/aged-receivables", label: "Aged receivables", icon: ChartLineUp, roles: ["founder", "accountant", "dispatch"] },
        // Founder-only finance reports
        { to: "/reports/expenses", label: "Expenses", icon: ChartLineUp, roles: ["founder", "accountant"] },
        { to: "/reports/gst", label: "Compliance", icon: ChartLineUp, roles: ["founder", "accountant"] },
        { to: "/reports/pg-recon", label: "Gateway recon", icon: ChartLineUp, roles: ["founder", "accountant"] },
        { to: "/reports/gstr-2b", label: "GSTR-2B recon", icon: ChartLineUp, roles: ["founder", "accountant"] },
        { to: "/reports-ca", label: "Reports for CA", icon: DownloadSimple, roles: ["founder", "accountant"] },
        { to: "/recurring-expenses", label: "Recurring expenses", icon: House, roles: ["founder", "accountant"] },
        { to: "/stock", label: "Stock (periodic)", icon: Package, roles: ["founder", "accountant"] },
        { to: "/reports/monthly-pnl", label: "Monthly P&L", icon: ChartLineUp, roles: ["founder"] },
        { to: "/reports/networth", label: "Net worth", icon: ChartLineUp, roles: ["founder", "accountant"] },
        { to: "/assets", label: "Fixed assets", icon: Cube, roles: ["founder"] },
        { to: "/chart-of-accounts", label: "Chart of accounts", icon: Notepad, roles: ["founder", "accountant"] },
    ]},
    { section: "Loans", accent: "#6f42c1", items: [
        { to: "/loans", label: "Loans", icon: Bank, roles: ["founder"] },
    ]},
    { section: "Admin", accent: "#d63384", items: [
        { to: "/users", label: "Users & roles", icon: Users, roles: ["founder"] },
        { to: "/bulk-imports", label: "Bulk imports", icon: UploadSimple, roles: ["founder"] },
        { to: "/audit-log", label: "Audit log", icon: ClockCounterClockwise, roles: ["founder", "accountant"] },
        { to: "/settings", label: "Settings", icon: GearSix, roles: ["founder", "accountant"] },
    ]},
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const [pwOpen, setPwOpen] = useState(false);
    const navigate = useNavigate();
    const role = user?.role;
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="min-h-screen flex" style={{ background: "var(--se-bg)" }}>
            {/* Mobile top bar */}
            <div
                className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3"
                style={{
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderBottom: "1px solid var(--se-border)",
                }}
                data-testid="mobile-topbar"
            >
                <button
                    onClick={() => setMobileOpen(true)}
                    className="p-2 rounded-lg"
                    style={{ background: "var(--se-brand-grad)", color: "#fff" }}
                    data-testid="mobile-menu-btn"
                    aria-label="Open menu"
                >
                    <List size={20} weight="bold" />
                </button>
                <div className="flex items-center gap-2">
                    <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: "var(--se-brand-grad)" }}
                    />
                    <div className="font-serif text-lg">Sixth Element</div>
                </div>
                <div
                    className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold"
                    style={{ background: "var(--se-brand-grad)", color: "#fff" }}
                    title={user?.name}
                >
                    {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
            </div>

            {/* Mobile drawer overlay */}
            {mobileOpen ? (
                <div
                    className="lg:hidden fixed inset-0 z-40"
                    style={{ background: "rgba(23, 18, 53, 0.4)", backdropFilter: "blur(4px)" }}
                    onClick={() => setMobileOpen(false)}
                    data-testid="mobile-drawer-overlay"
                />
            ) : null}

            <aside
                className={`
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
                    fixed lg:sticky top-0 left-0 z-50 lg:z-auto
                    w-64 shrink-0 flex flex-col
                    transition-transform duration-300 ease-out
                `}
                style={{
                    background: "linear-gradient(180deg, #170f2e 0%, #1c1042 55%, #2d1259 100%)",
                    color: "#f8fafc",
                    height: "100vh",
                    overflowY: "auto",
                }}
                data-testid="sidebar"
            >
                <div
                    className="px-5 pt-6 pb-4"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                >
                    <div className="flex items-center gap-2.5">
                        <span
                            className="inline-block w-3 h-3 rounded-md"
                            style={{ background: "var(--se-brand-grad)", boxShadow: "0 0 20px rgba(244, 63, 94, 0.6)" }}
                        />
                        <div className="font-serif text-xl" style={{ color: "#f8fafc" }} data-testid="brand-name">
                            Sixth Element
                        </div>
                    </div>
                    <div className="text-[10px] tracking-widest uppercase mt-1.5" style={{ color: "#a89dc9" }}>
                        Ledger &amp; Money
                    </div>
                </div>
                <nav className="py-3 flex-1">
                    {NAV.map((sec) => {
                        const visible = sec.items.filter((i) => !i.roles || i.roles.includes(role));
                        if (!visible.length) return null;
                        return (
                            <div key={sec.section}>
                                <div className="se-nav-section" style={{ color: sec.accent }}>{sec.section}</div>
                                {visible.map((it) => {
                                    const Icon = it.icon;
                                    return (
                                        <NavLink
                                            key={it.to}
                                            to={it.to}
                                            end={it.to === "/"}
                                            onClick={() => setMobileOpen(false)}
                                            className={({ isActive }) => `se-nav-link ${isActive ? "active" : ""}`}
                                            data-testid={`nav-${it.to.replace(/\//g, "-").replace(/^-/, "") || "home"}`}
                                        >
                                            <Icon weight="duotone" size={17} style={{ color: sec.accent }} />
                                            <span>{it.label}</span>
                                        </NavLink>
                                    );
                                })}
                            </div>
                        );
                    })}
                </nav>
                <div
                    className="px-5 py-4"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.25)" }}
                >
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold"
                            style={{ background: "var(--se-brand-grad)", color: "#fff" }}
                        >
                            {user?.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs truncate" style={{ color: "#f8fafc" }} data-testid="current-user-name">
                                {user?.name}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#a89dc9" }} data-testid="current-user-role">
                                {user?.role}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                        <button
                            onClick={() => setPwOpen(true)}
                            className="flex items-center gap-1.5 hover:text-white transition-colors"
                            style={{ color: "#c5b8e0" }}
                            data-testid="change-pw-btn"
                            title="Change your password"
                        >
                            <Key size={13} /> Change password
                        </button>
                        <button
                            onClick={async () => {
                                if (!window.confirm("Sign out of every device you're logged in on?")) return;
                                await logout({ everywhere: true });
                                navigate("/login");
                            }}
                            className="flex items-center gap-1.5 hover:text-white transition-colors"
                            style={{ color: "#c5b8e0" }}
                            data-testid="logout-all-btn"
                            title="Revoke all your sessions everywhere"
                        >
                            <ShieldCheck size={13} /> Sign out everywhere
                        </button>
                        <button
                            onClick={async () => { await logout(); navigate("/login"); }}
                            className="flex items-center gap-1.5 hover:text-white transition-colors ml-auto"
                            style={{ color: "#c5b8e0" }}
                            data-testid="logout-btn"
                        >
                            <SignOut size={13} /> Sign out
                        </button>
                    </div>
                </div>
            </aside>
            {pwOpen ? <ChangePasswordDialog onClose={() => setPwOpen(false)} /> : null}
            <main
                className="flex-1 min-w-0"
                data-testid="main-content"
                style={{ background: "transparent", paddingTop: mobileOpen ? 0 : undefined }}
            >
                {/* Spacer for the mobile top bar (fixed) */}
                <div className="lg:hidden" style={{ height: 56 }} />
                <Breadcrumbs />
                {children}
            </main>
        </div>
    );
}


const PASSWORD_HINTS = [
    "At least 10 characters",
    "Mix of upper + lower case letters",
    "At least one digit",
    "At least one symbol (! @ # $ …)",
    "Not a common or breached password",
];

function ChangePasswordDialog({ onClose }) {
    const { user, changePassword } = useAuth();
    const navigate = useNavigate();
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);

    const checks = {
        len: pw.length >= 10,
        upper: /[A-Z]/.test(pw),
        lower: /[a-z]/.test(pw),
        digit: /\d/.test(pw),
        sym: /[^A-Za-z0-9]/.test(pw),
    };
    const allGood = Object.values(checks).every(Boolean) && pw === pw2 && pw.length > 0;

    const submit = async () => {
        if (!allGood) return;
        setBusy(true);
        const res = await changePassword(pw);
        setBusy(false);
        if (res.ok) {
            toast.success("Password updated — please sign in again with your new password.");
            onClose();
            navigate("/login");
        } else {
            toast.error(res.error || "Password change failed");
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="change-pw-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Change your password</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-xs text-stone-500">
                        Signed in as <b>{user?.email}</b>. Your other devices will be signed out.
                    </div>
                    <div>
                        <Label>New password</Label>
                        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                               autoFocus data-testid="pw-new" autoComplete="new-password" />
                    </div>
                    <div>
                        <Label>Confirm new password</Label>
                        <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                               data-testid="pw-confirm" autoComplete="new-password" />
                    </div>
                    <ul className="text-[11px] space-y-1">
                        {[
                            { ok: checks.len, txt: PASSWORD_HINTS[0] },
                            { ok: checks.upper && checks.lower, txt: PASSWORD_HINTS[1] },
                            { ok: checks.digit, txt: PASSWORD_HINTS[2] },
                            { ok: checks.sym, txt: PASSWORD_HINTS[3] },
                            { ok: pw === pw2 && pw.length > 0, txt: "Both entries match" },
                        ].map((c, i) => (
                            <li key={i} className={c.ok ? "text-emerald-600" : "text-stone-400"}>
                                {c.ok ? "✓" : "○"} {c.txt}
                            </li>
                        ))}
                    </ul>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !allGood}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="pw-submit">
                        {busy ? "…" : "Update password"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
